import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Eye,
  Heart,
  MapPin,
  Plus,
  Shield,
  Star,
  UserCog,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import CareOfficerProfileModal from '../../components/shared/CareOfficerProfileModal';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  formatWatDate,
  formatWatDateTime,
  getStatusClasses,
  getVisitStart,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';
import { getProfilePhotoUrl } from '../../lib/profileUtils';

const WellnessRing = ({ score, size = 64, showLabel = true }) => {
  const value = typeof score === 'number' ? score : 0;
  const color = value >= 75 ? '#22c55e' : value >= 50 ? '#eab308' : value >= 25 ? '#f97316' : '#ef4444';
  const radius = (size / 2) - 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  const label = value >= 75 ? 'Excellent' : value >= 50 ? 'Good' : value >= 25 ? 'Watch' : 'Urgent';

  return (
    <div className="relative flex flex-col items-center" style={{ width: size, minHeight: showLabel ? size + 18 : size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E8E6E3" strokeWidth="6" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-x-0 top-0 flex items-center justify-center font-serif text-lg font-semibold" style={{ height: size, color }}>
        {value || '-'}
      </div>
      {showLabel && <span className="text-xs text-[#5A5A5A]">{label}</span>}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, note, tone = 'burgundy' }) => {
  const tones = {
    burgundy: 'text-[#9F1D35] bg-[#F5E8EA]',
    green: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50',
    red: 'text-red-700 bg-red-50',
  };
  return (
    <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-full ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-serif text-3xl font-semibold tabular-nums text-[#1A1A1A]">{value}</p>
      <p className="mt-1 text-sm text-[#5A5A5A]">{label}</p>
      {note && <p className="mt-2 text-xs text-[#9F1D35]">{note}</p>}
    </div>
  );
};

const hasHomePin = (dependant) => Boolean(
  dependant?.address?.coordinates &&
  (
    dependant.address.coordinates.latitude !== undefined ||
    dependant.address.coordinates.lat !== undefined
  ) &&
  (
    dependant.address.coordinates.longitude !== undefined ||
    dependant.address.coordinates.lng !== undefined
  )
);

const formatCurrency = (amount, currency = 'NGN') => {
  if (amount === null || amount === undefined || amount === '') return 'Amount not set';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
};

const getWellnessScore = (record) => {
  const raw = record?.overallWellnessScore ?? record?.overall_wellness_score;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const SubscriberDashboard = () => {
  const navigate = useNavigate();
  const [selectedCareOfficer, setSelectedCareOfficer] = useState(null);
  const {
    currentUser,
    getSubscriberDependants,
    getSubscriberAlerts,
    getSubscriberVisits,
    getPendingCareExpenses,
    getCareOfficerById,
    startCareOfficerConversation,
    approveCareExpense,
    declineCareExpense,
  } = useApp();

  const dependants = getSubscriberDependants(currentUser?.id);
  const alerts = getSubscriberAlerts(currentUser?.id);
  const visits = useMemo(() => sortVisitsNewestFirst(getSubscriberVisits(currentUser?.id)), [currentUser?.id, getSubscriberVisits]);
  const pendingCareExpenses = getPendingCareExpenses(currentUser?.id);
  const newAlerts = alerts.filter(alert => alert.status === 'New');
  const criticalAlerts = alerts.filter(alert => alert.severity === 'Critical' && alert.status !== 'Resolved');
  const missingLocationPins = dependants.filter(dependant => !hasHomePin(dependant));
  const recentVisits = visits.slice(0, 4);
  const visitsThisMonth = visits.filter(visit => {
    const date = getVisitStart(visit);
    const now = new Date();
    return date && visit.status !== 'Scheduled' && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;
  const wellnessScores = dependants
    .map(getWellnessScore)
    .filter(score => score !== null);
  const avgWellness = wellnessScores.length
    ? Math.round(wellnessScores.reduce((sum, score) => sum + score, 0) / wellnessScores.length)
    : 0;

  const latestVisitFor = (dependantId) => visits.find(visit => visit.dependantId === dependantId);
  const upcomingVisitFor = (dependantId) => visits
    .filter(visit => visit.dependantId === dependantId && visit.status === 'Scheduled')
    .sort((a, b) => (getVisitStart(a)?.getTime() || 0) - (getVisitStart(b)?.getTime() || 0))[0];
  const assignedCareOfficers = useMemo(() => {
    const byId = new Map();
    dependants.forEach((dependant) => {
      const officerId = dependant.assignedCareOfficer || dependant.assigned_care_officer;
      if (!officerId) return;
      const officer = getCareOfficerById(officerId);
      if (officer) byId.set(officerId, { officer, dependant });
    });
    visits.forEach((visit) => {
      const officerId = visit.careOfficerId || visit.care_officer_id;
      if (!officerId || byId.has(officerId)) return;
      const officer = getCareOfficerById(officerId) || visit.careOfficerSnapshot || visit.care_officer_snapshot;
      if (officer) byId.set(officerId, { officer, visit });
    });
    return Array.from(byId.values());
  }, [dependants, getCareOfficerById, visits]);

  const handleExpenseDecision = async (expense, action) => {
    try {
      if (action === 'approve') {
        await approveCareExpense(expense.id);
        toast.success('Care expense approved.');
      } else {
        await declineCareExpense(expense.id);
        toast.success('Care expense declined.');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to update care expense.'));
    }
  };

  const openCareOfficerChat = async ({ officerId }) => {
    const conversation = await startCareOfficerConversation(officerId);
    setSelectedCareOfficer(null);
    navigate(`/messages?conversation=${conversation.id}`);
  };

  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 17 ? 'Good afternoon' : 'Good evening';
  const displayName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="subscriber-dashboard">
      <header className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-7 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#9F1D35] text-lg font-semibold text-[#FAFAF7]">
              {currentUser?.firstName?.[0] || 'M'}{currentUser?.lastName?.[0] || ''}
            </div>
            <div>
              <p className="text-sm text-[#5A5A5A]">{greeting}</p>
              <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A]">{displayName || 'Welcome'}</h1>
              <p className="mt-1 flex items-center gap-2 text-sm text-[#5A5A5A]">
                <Shield className="h-4 w-4" />
                {currentUser?.plan || 'Care'} plan
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {criticalAlerts.length > 0 && (
              <button type="button" onClick={() => navigate('/alerts')} className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700">
                <AlertTriangle className="h-4 w-4" />
                {criticalAlerts.length} critical
              </button>
            )}
            <button type="button" onClick={() => navigate('/dependants/add')} className="inline-flex items-center gap-2 rounded-full bg-[#9F1D35] px-5 py-2.5 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]">
              <Plus className="h-4 w-4" />
              Add Dependant
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
        {missingLocationPins.length > 0 && (
          <button
            type="button"
            onClick={() => navigate('/dependants')}
            className="mb-6 flex w-full items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left text-amber-900"
          >
            <span className="flex items-center gap-3 text-sm">
              <MapPin className="h-5 w-5" />
              {missingLocationPins.length} dependant{missingLocationPins.length === 1 ? '' : 's'} need confirmed home location pins for GPS verification.
            </span>
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <section className="mb-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Users} label="Dependants" value={dependants.length} />
          <Stat icon={Activity} label="Average wellness" value={avgWellness || '-'} tone={avgWellness >= 50 ? 'green' : 'amber'} />
          <Stat icon={Bell} label="Active alerts" value={newAlerts.length} tone={newAlerts.length ? 'amber' : 'green'} note={criticalAlerts.length ? `${criticalAlerts.length} critical` : ''} />
          <Stat icon={Wallet} label="Pending approvals" value={pendingCareExpenses.length} tone={pendingCareExpenses.length ? 'amber' : 'burgundy'} />
        </section>

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <div className="flex items-center justify-between border-b border-[#E8E6E3] px-5 py-4">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Your Loved Ones</h2>
                  <p className="text-sm text-[#5A5A5A]">Current care status from registered dependants</p>
                </div>
                <button type="button" onClick={() => navigate('/dependants')} className="text-sm font-medium text-[#9F1D35]">View all</button>
              </div>
              <div className="divide-y divide-[#E8E6E3]">
                {dependants.map(dependant => {
                  const latest = latestVisitFor(dependant.id);
                  const upcoming = upcomingVisitFor(dependant.id);
                  return (
                    <div key={dependant.id} className="p-5" data-testid={`dependant-row-${dependant.id}`}>
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F5E8EA] font-semibold text-[#9F1D35]">
                          {dependant.firstName?.[0]}{dependant.lastName?.[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-medium text-[#1A1A1A]">{dependant.firstName} {dependant.lastName}</h3>
                            {!hasHomePin(dependant) && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Confirm location</span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-[#5A5A5A]">{dependant.relationship} - {dependant.age} years</p>
                          <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#5A5A5A]">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              Last: {latest ? formatWatDate(getVisitStart(latest)) : 'No visits yet'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              Next: {upcoming ? formatWatDate(getVisitStart(upcoming)) : 'Not scheduled'}
                            </span>
                          </div>
                        </div>
                        <WellnessRing score={getWellnessScore(dependant)} showLabel={false} />
                        <button type="button" onClick={() => navigate(`/visits?dependant=${dependant.id}`)} className="rounded-full p-2 text-[#9F1D35] hover:bg-[#F5E8EA]">
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {dependants.length === 0 && (
                  <div className="p-8 text-center">
                    <Users className="mx-auto mb-3 h-10 w-10 text-[#D8D5D0]" />
                    <p className="font-medium text-[#1A1A1A]">No dependants registered yet</p>
                    <button type="button" onClick={() => navigate('/dependants/add')} className="mt-3 rounded-full bg-[#9F1D35] px-4 py-2 text-sm font-medium text-[#FAFAF7]">Add dependant</button>
                  </div>
                )}
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <div className="flex items-center justify-between border-b border-[#E8E6E3] px-5 py-4">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Recent Visit Reports</h2>
                  <p className="text-sm text-[#5A5A5A]">{visitsThisMonth} completed or submitted this month</p>
                </div>
                <button type="button" onClick={() => navigate('/visits')} className="text-sm font-medium text-[#9F1D35]">View all</button>
              </div>
              <div className="grid gap-4 p-5 md:grid-cols-2">
                {recentVisits.map(visit => (
                  <button key={visit.id} type="button" onClick={() => navigate(`/visits/${visit.id}`)} className="rounded-2xl border border-[#E8E6E3] bg-white p-4 text-left hover:border-[#D8D5D0]">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{visit.dependantName || visit.dependantSnapshot?.name || 'Dependant'}</p>
                        <p className="mt-1 text-xs text-[#5A5A5A]">{formatWatDateTime(getVisitStart(visit))}</p>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>{visit.status}</span>
                    </div>
                    <p className="mt-3 text-sm text-[#5A5A5A] line-clamp-2">{visit.summary || visit.checklist?.generalComments || 'Visit report available.'}</p>
                  </button>
                ))}
                {recentVisits.length === 0 && <p className="p-4 text-sm text-[#5A5A5A]">No visit reports yet.</p>}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <div className="flex items-center justify-between border-b border-[#E8E6E3] px-5 py-4">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Assigned Care Officers</h2>
                  <p className="text-sm text-[#5A5A5A]">Tap a profile to view or contact them</p>
                </div>
                <UserCog className="h-5 w-5 text-[#9F1D35]" />
              </div>
              <div className="divide-y divide-[#E8E6E3]">
                {assignedCareOfficers.slice(0, 5).map(({ officer, dependant, visit }, index) => {
                  const name = officer.name || `${officer.firstName || officer.first_name || ''} ${officer.lastName || officer.last_name || ''}`.trim() || visit?.careOfficerName || 'Care officer';
                  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'CO';
                  const profilePhotoUrl = getProfilePhotoUrl(officer) || getProfilePhotoUrl(visit?.careOfficerSnapshot || visit?.care_officer_snapshot);
                  const assignedFor = dependant
                    ? `${dependant.firstName || dependant.first_name || 'Dependant'} ${dependant.lastName || dependant.last_name || ''}`.trim()
                    : visit?.dependantName || visit?.dependantSnapshot?.name || 'Recent visit';
                  return (
                    <button
                      key={officer.id || officer._id || index}
                      type="button"
                      onClick={() => setSelectedCareOfficer(officer)}
                      className="flex w-full items-center gap-3 p-4 text-left hover:bg-[#F7F4EE]"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#9F1D35] text-sm font-semibold text-[#FAFAF7]">
                        {profilePhotoUrl ? <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" /> : initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#1A1A1A]">{name}</span>
                        <span className="block truncate text-xs text-[#5A5A5A]">Assigned to {assignedFor}</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs font-medium text-[#9F1D35]">
                        <Star className="h-3.5 w-3.5 fill-[#C9A961] text-[#C9A961]" />
                        {officer.rating || '-'}
                      </span>
                    </button>
                  );
                })}
                {assignedCareOfficers.length === 0 && (
                  <p className="p-5 text-sm text-[#5A5A5A]">No care officer has been assigned yet.</p>
                )}
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <div className="flex items-center justify-between border-b border-[#E8E6E3] px-5 py-4">
                <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Alerts</h2>
                <span className="rounded-full bg-[#F5E8EA] px-2 py-0.5 text-xs font-medium text-[#9F1D35]">{alerts.length} active</span>
              </div>
              <div className="divide-y divide-[#E8E6E3]">
                {alerts.slice(0, 5).map(alert => (
                  <button key={alert.id} type="button" onClick={() => navigate('/alerts')} className="block w-full p-4 text-left hover:bg-[#F7F4EE]" data-testid={`alert-item-${alert.id}`}>
                    <div className="flex items-start gap-3">
                      <span className={`mt-2 h-2 w-2 rounded-full ${alert.severity === 'Critical' ? 'bg-red-600' : alert.severity === 'Warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">{alert.message}</p>
                        <p className="mt-1 text-xs text-[#5A5A5A]">{alert.category} - {alert.status}</p>
                      </div>
                    </div>
                  </button>
                ))}
                {alerts.length === 0 && <p className="p-5 text-sm text-[#5A5A5A]">No active alerts.</p>}
              </div>
            </section>

            <section className="rounded-2xl bg-[#9F1D35] p-5 text-[#FAFAF7]">
              <div className="mb-3 flex items-center gap-3">
                <Heart className="h-5 w-5" />
                <h3 className="font-medium">Need Assistance?</h3>
              </div>
              <p className="text-sm text-[#F5E8EA]">Our care team is available to help with concerns about your loved ones.</p>
              <button type="button" className="mt-4 w-full rounded-full bg-[#FAFAF7] px-4 py-2.5 text-sm font-medium text-[#9F1D35]">Contact Support</button>
            </section>
          </aside>
        </div>

        <section className="mt-6 overflow-hidden rounded-2xl border border-amber-200 bg-[#FAFAF7]">
          <div className="flex items-center justify-between border-b border-amber-200 bg-amber-50 px-5 py-4">
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Pending Approvals</h2>
              <p className="text-sm text-[#5A5A5A]">Care expenses awaiting your approval after supervisor review</p>
            </div>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">{pendingCareExpenses.length} pending</span>
          </div>
          <div className="grid gap-4 p-5 md:grid-cols-2 lg:grid-cols-3">
            {pendingCareExpenses.map(expense => (
              <div key={expense.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid={`pending-care-expense-${expense.id}`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{expense.title}</p>
                    <p className="text-xs text-[#5A5A5A]">For {expense.dependantName}</p>
                  </div>
                  <p className="font-serif text-xl font-semibold text-[#1A1A1A]">{formatCurrency(expense.estimatedAmount, expense.currency)}</p>
                </div>
                <p className="mb-4 line-clamp-3 text-sm text-[#5A5A5A]">{expense.explanation}</p>
                <p className="mb-4 text-xs text-[#5A5A5A]">Requested by {expense.careOfficerName} - {expense.category}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => handleExpenseDecision(expense, 'approve')} className="inline-flex flex-1 items-center justify-center gap-1 rounded-full bg-emerald-700 px-3 py-2 text-sm font-medium text-white">
                    <CheckCircle2 className="h-4 w-4" />
                    Approve
                  </button>
                  <button type="button" onClick={() => handleExpenseDecision(expense, 'decline')} className="inline-flex flex-1 items-center justify-center gap-1 rounded-full border border-[#D8D5D0] bg-white px-3 py-2 text-sm font-medium text-[#5A5A5A]">
                    <XCircle className="h-4 w-4" />
                    Decline
                  </button>
                </div>
              </div>
            ))}
            {pendingCareExpenses.length === 0 && <p className="text-sm text-[#5A5A5A]">No care expenses are awaiting approval.</p>}
          </div>
        </section>
      </main>
      {selectedCareOfficer && (
        <CareOfficerProfileModal
          officer={selectedCareOfficer}
          canRate={false}
          onStartChat={openCareOfficerChat}
          onClose={() => setSelectedCareOfficer(null)}
        />
      )}
    </div>
  );
};

export default SubscriberDashboard;
